/** Parse / serialize supplier-risk email lists stored in company_settings.customizations */

export type SupplierRiskEmailFormState = {
  fromEmail: string
  intakeSubmitTo: string
  intakeSubmitCc: string
  intakeApprovedTo: string
  intakeApprovedCc: string
  alertDay1Cc: string
  alertOnSubmitTo: string
  remindOnDays: string
  dueByDay: string
}

export const EMPTY_SUPPLIER_RISK_EMAIL_FORM: SupplierRiskEmailFormState = {
  fromEmail: 'Early Signals <noreply@theearlysignals.com>',
  intakeSubmitTo: '',
  intakeSubmitCc: '',
  intakeApprovedTo: '',
  intakeApprovedCc: '',
  alertDay1Cc: '',
  alertOnSubmitTo: '',
  remindOnDays: '3, 5',
  dueByDay: '5',
}

export function emailsToText(arr?: unknown): string {
  if (!Array.isArray(arr)) return ''
  return arr.map((e) => String(e).trim()).filter(Boolean).join(', ')
}

export function textToEmails(text: string): string[] {
  return text
    .split(/[,;\n]+/)
    .map((e) => e.trim())
    .filter(Boolean)
}

export function daysToText(arr?: unknown, fallback = '3, 5'): string {
  if (!Array.isArray(arr) || !arr.length) return fallback
  return arr
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n) && n > 0)
    .join(', ')
}

export function textToDays(text: string, fallback: number[] = [3, 5]): number[] {
  const parsed = [
    ...new Set(
      text
        .split(/[,;\s]+/)
        .map((n) => Number(n.trim()))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ].sort((a, b) => a - b)
  return parsed.length ? parsed : fallback
}

export function loadSupplierRiskEmailForm(customizations?: Record<string, any> | null): SupplierRiskEmailFormState {
  const intake = customizations?.supplierRiskIntake || {}
  const alert = customizations?.supplierRiskAlertFeedback || {}
  const formDay = Number(alert.feedbackForm?.sendOnDay ?? 3)
  const dueBy = Number(alert.feedbackForm?.dueByDay ?? 5)
  const remindRaw = alert.feedbackForm?.remindOnDays ?? alert.feedbackForm?.actionDays
  const remindDays = Array.isArray(remindRaw) && remindRaw.length
    ? remindRaw
    : [formDay, dueBy]

  return {
    fromEmail: String(intake.fromEmail || alert.fromEmail || EMPTY_SUPPLIER_RISK_EMAIL_FORM.fromEmail),
    intakeSubmitTo: emailsToText(intake.submitNotify?.to),
    intakeSubmitCc: emailsToText(intake.submitNotify?.cc),
    intakeApprovedTo: emailsToText(intake.approvedNotify?.to),
    intakeApprovedCc: emailsToText(intake.approvedNotify?.cc),
    alertDay1Cc: emailsToText(alert.day1?.cc),
    alertOnSubmitTo: emailsToText(alert.onSubmitNotify?.to),
    remindOnDays: daysToText(remindDays),
    dueByDay: String(dueBy || 5),
  }
}

export function mergeSupplierRiskEmailCustomizations(
  customizations: Record<string, any> | null | undefined,
  form: SupplierRiskEmailFormState,
): Record<string, any> {
  const prev = customizations || {}
  const prevIntake = prev.supplierRiskIntake || {}
  const prevAlert = prev.supplierRiskAlertFeedback || {}
  const remindOnDays = textToDays(form.remindOnDays)
  const dueByDay = Number(form.dueByDay) || Math.max(...remindOnDays, 5)
  const sendOnDay = remindOnDays[0] ?? 3
  const fromEmail = form.fromEmail.trim() || EMPTY_SUPPLIER_RISK_EMAIL_FORM.fromEmail

  return {
    ...prev,
    supplierRiskIntake: {
      ...prevIntake,
      enabled: prevIntake.enabled !== false,
      fromEmail,
      fromName: prevIntake.fromName || 'Early Signals',
      submitNotify: {
        ...(prevIntake.submitNotify || {}),
        to: textToEmails(form.intakeSubmitTo),
        cc: textToEmails(form.intakeSubmitCc),
        // Approve/Reject links must not go to the requestor
        ccRequester: false,
      },
      approvedNotify: {
        ...(prevIntake.approvedNotify || {}),
        to: textToEmails(form.intakeApprovedTo),
        cc: textToEmails(form.intakeApprovedCc),
        // Requestor gets a dedicated decision email from the edge function
        ccRequester: false,
      },
      rejectedNotify: {
        ...(prevIntake.rejectedNotify || prevIntake.approvedNotify || {}),
        to: textToEmails(form.intakeApprovedTo),
        cc: textToEmails(form.intakeApprovedCc),
        ccRequester: false,
      },
      approval: prevIntake.approval || { required: true },
      labels: prevIntake.labels || {},
    },
    supplierRiskAlertFeedback: {
      ...prevAlert,
      enabled: prevAlert.enabled !== false,
      fromEmail,
      day1: {
        ...(prevAlert.day1 || {}),
        cc: textToEmails(form.alertDay1Cc),
      },
      feedbackForm: {
        ...(prevAlert.feedbackForm || {}),
        sendOnDay,
        dueByDay,
        remindOnDays,
        remindDaily: false,
      },
      onSubmitNotify: {
        ...(prevAlert.onSubmitNotify || {}),
        to: textToEmails(form.alertOnSubmitTo),
        ccCategoryManager: prevAlert.onSubmitNotify?.ccCategoryManager !== false,
      },
    },
  }
}
